import { FC, useEffect, useState } from 'react';
import { TextField, withStyles, Card, Tooltip, FieldData, Box } from 'vopay';
import styles from './CreateClientAccountPage.module.scss';
import CreateCustomerForm from '@/components/Customer/CreateCustomer/CreateCustomerForm';
import { useTranslation } from 'react-i18next';
import { UtilButton, Title, Toggle, Checkbox } from 'UI';
import { useHistory, useLocation } from 'react-router-dom';
import { useClientData } from '@/providers/ClientData.provider';
import { FormValues, SerializedFormValues } from '@/components/Customer/CreateCustomer/useFormConfig';
import { useToast } from '@/providers/Toast.provider';
import { useDataFetchIndicator } from '@/hooks';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import useCportal from '@/hooks/useApi/useCportal';

const cn = withStyles(styles);

type CreateClientAccountPageProps = {};

type ClientAccountFormValues = {
  identificationNumber: string;
};

type LocationState = {
  showBackButton?: boolean;
};

const CreateClientAccountPage: FC<CreateClientAccountPageProps> = () => {
  const {
    response: { Country: loginUserCountry },
  } = useClientData();

  const history = useHistory();
  const location = useLocation<LocationState>();
  const { state } = location;
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.Create' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [receiveOnly, setReceiveOnly] = useState(false);
  const [shouldEnableCportal, setShouldEnableCportal] = useState(false);
  const [shouldSendAccess, setShouldSendAccess] = useState(false);
  const { createClientAccount } = useClientAccounts();
  const [reviewing, setReviewing] = useState(false);
  const { services, addCportalUser, fetchAccountCportalSettings } = useCportal();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const toast = useToast();

  useEffect(() => {
    const getSettings = async () => {
      try {
        setDataFetchingInfo(true);
        await fetchAccountCportalSettings();
      } catch (err) {
        toast.neutral(
          t('fetchCportalSettingsError', {
            defaultValue: 'Failed to fetch cPortal settings',
          }),
        );
      } finally {
        setDataFetchingInfo(false);
      }
    };

    getSettings();
  }, []);

  const serviceLabelMap = {
    Fund: t('fund', { defaultValue: 'Fund' }),
    Withdraw: t('withdraw', { defaultValue: 'Withdraw' }),
    Transfer: t('transfer', { defaultValue: 'Transfer' }),
    'Global Send': t('globalSend', { defaultValue: 'Global Send' }),
  };

  const serviceOrder = ['Fund', 'Withdraw', 'Transfer', 'Global Send'];

  const moreInfoConfig: FieldData<FormValues & ClientAccountFormValues>[] = [
    {
      label: () => t('identificationNumber'),
      fieldName: 'identificationNumber',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <TextField
            {...getFormikData<ClientAccountFormValues['identificationNumber']>({})}
            variant="outlineShadow"
            preset="positiveInteger"
          />
        );
      },
    },
  ];

  async function handleCreateAccount(values: SerializedFormValues & ClientAccountFormValues) {
    try {
      const res = await createClientAccount(
        {
          FirstName: values.firstName,
          LastName: values.lastName,
          EmailAddress: values.email,
          PhoneNumber: values.phone,
          BusinessName: values.businessName,
          ClientAccountID: values.accountID,
          Label: values.label,
          Address1: values.address1,
          City: values.city,
          Province: values.province,
          PostalCode: values.postalCode,
          Country: values.country,
          Currency: loginUserCountry === 'CA' ? 'CAD' : 'USD',
          IdentificationNumber: values.identificationNumber,
          FlinksAccountID: values.flinksAccountID,
          FlinksLoginID: values.flinksLoginID,
          PlaidProcessorToken: values.plaidProcessorToken,
        },
        receiveOnly,
      );
      if (shouldEnableCportal) {
        try {
          setDataFetchingInfo(true);
          await addCportalUser({
            PhoneNumber: values.phone,
            Email: values.email,
            ClientAccountID: res.clientAccountID,
            SendWelcomeEmail: shouldSendAccess,
          });

          return res.clientAccountID;
        } catch (err) {
          toast.neutral(
            t('enableCportalError', {
              defaultValue: 'Failed to enable cPortal',
            }),
          );
          throw err;
        } finally {
          setDataFetchingInfo(false);
        }
      }
      return res.clientAccountID;
    } catch (err) {
      throw err;
    }
  }

  const showBackButton = state?.showBackButton;

  return (
    <div className={cn('createClientAccountPage')}>
      {showBackButton && (
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            if (history.length > 1) {
              history.goBack();
            } else {
              history.replace('/client-accounts');
            }
          }}
        >
          {history.length > 1 ? tGlobal('Buttons.back') : t('clientAccountTitle')}
        </UtilButton>
      )}
      <Title className={cn('title')}>{t('pageTitle', { label: t('clientAccount') })}</Title>

      <div className={cn('form')}>
        <CreateCustomerForm
          moreInfoConfig={moreInfoConfig}
          moreInfoInitialValues={{
            identificationNumber: '',
          }}
          customerLabel={t('clientAccount')}
          createAccount={handleCreateAccount}
          onComplete={(accountID, search) => {
            history.push({
              pathname: `/client-accounts/${accountID}`,
              search: search ? new URLSearchParams(search).toString() : undefined,
              state: shouldEnableCportal ? { showLinkDialog: true } : undefined,
            });
          }}
          reviewing={reviewing}
          onReviewChange={setReviewing}
          shouldEnableCportal={shouldEnableCportal}
        />

        <div>
          <Card className={cn('clientAccountSettings', reviewing && 'reviewing')}>
            <Box flx="col" gapSize="32px">
              <Toggle
                name="receive-only-check"
                checked={receiveOnly}
                onChange={(e) => {
                  setReceiveOnly(e.target.checked);
                }}
                label={
                  <Box gapSize="4px" ai="center">
                    <div>{t('receiveOnly', { defaultValue: 'Receive Only Mode' })}</div>
                    <Tooltip
                      icon="info"
                      position="top"
                      information={t('receiveOnlyTooltip', {
                        defaultValue: 'Limits the client account to inbound transactions only.',
                      })}
                    />
                  </Box>
                }
              />
              <Box flx="col" gapSize="16px">
                <Box flx="col" gapSize="4px">
                  <Toggle
                    name="enable-cportal-check"
                    checked={shouldEnableCportal}
                    onChange={(e) => {
                      setShouldEnableCportal(e.target.checked);
                    }}
                    label={
                      <Box gapSize="4px" ai="center">
                        <div>{t('enableCportal', { defaultValue: 'Enable cPortal' })}</div>
                        <Tooltip
                          icon="info"
                          position="top"
                          information={t('enableCportalTooltip', {
                            defaultValue: 'Enables access to cPortal. Once enabled, it can not be disabled.',
                          })}
                        />
                      </Box>
                    }
                  />
                  <div className={cn('description')}>
                    {t('enableCportalDescription', { defaultValue: 'Once enabled, it can not be disabled.' })}
                  </div>
                </Box>

                {/* all services are read-only until we enable them for each client account*/}
                {shouldEnableCportal && (
                  <Box flx="col" gapSize="24px">
                    <Card className={cn('serviceContainer')} hoverEffect={false}>
                      <Title level="6" className={cn('serviceTitle')}>
                        {t('services', { defaultValue: 'Services' })}
                      </Title>
                      {services
                        .filter((service) => service.Service !== 'Global Send')
                        .sort((a, b) => serviceOrder.indexOf(a.Service) - serviceOrder.indexOf(b.Service))
                        .map((service) => (
                          <div key={service.Service} className={cn('toggleContainer')}>
                            <Toggle
                              name={`enable-${service.Service}`}
                              checked={service.Enabled}
                              readOnly
                              label={serviceLabelMap[service.Service]}
                            />
                          </div>
                        ))}
                    </Card>
                    <Box gapSize="6px">
                      <div>
                        <Checkbox
                          name="send-access-check"
                          checked={shouldSendAccess}
                          label={
                            <div className={cn('accessDescription')}>
                              {t('sendAccess', {
                                defaultValue: 'Send cPortal access to the client account via email address.',
                              })}
                            </div>
                          }
                          onChange={() => {
                            setShouldSendAccess(!shouldSendAccess);
                          }}
                        />
                      </div>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreateClientAccountPage;
